import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons/faExternalLinkAlt';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { Button, Flyout } from '@appcues/sonar';
import { Icon } from '@appcues/sonar-builder';
import { useEventBusEnabled } from '@appcues/event-bus';
import {
  selectUserProperties,
  userPropertiesShape,
} from 'ext/entities/user-properties';
import {
  create as createLocale,
  remove as removeLocale,
  update as updateLocale,
  Shape as LocalesShape,
  setLocale,
  selectActiveLocale,
  selectLocales,
} from 'ext/entities/locales';

import {
  getLanguageFromCode,
  DEFAULT_LOCALE,
  LOCALIZATION_BANNERS_LINK,
} from 'ext/lib/localization';
import { LocalesList } from './LocalesList';
import { AddLocale } from './AddLocale';

export const LocalesFlyout = ({
  locales = [],
  userProperties,
  status,
  open,
  portal,
  onClose,
  overrideSelect,
  onCreateLocale,
  onRemoveLocale,
  onUpdateLocale,
  onSetLocale,
  activeLocale,
  learnMoreLink = LOCALIZATION_BANNERS_LINK,
  elementRef, // Optional: EventBus ref to indicate when dialog should render
}) => {
  const isEventBusEnabled = useEventBusEnabled();
  const [isAddingLanguage, setIsAddingLanguage] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState();

  // When EventBus is enabled, elementRef presence indicates dialog should render
  // Otherwise, use the open prop for backward compatibility
  const shouldRender = isEventBusEnabled ? elementRef !== undefined : open;

  const handleFlyoutClose = () => {
    setIsAddingLanguage(false);
    setEditingLanguage();
    onClose();
  };

  const localesList = locales.map(({ id, name, conditions }) => {
    const { property, value } = conditions?.and[0].properties ?? {};

    const propertyName = userProperties?.find(
      userProperty => userProperty.value === property
    )?.name;

    const targetCriteriaNames =
      value
        ?.split('\n')
        ?.map(targetCriteriaCode => getLanguageFromCode(targetCriteriaCode)) ??
      [];

    return {
      id,
      name,
      property: propertyName,
      targetCriteria: targetCriteriaNames,
    };
  });

  const handleRemoveLocale = id => {
    onRemoveLocale(id);
    // Only reset to default locale if the deleted locale is the currently active one
    if (activeLocale?.id === id) {
      onSetLocale(DEFAULT_LOCALE.id);
    }
  };

  // Don't render if not open/shown
  if (!shouldRender) return null;

  // In EventBus mode, always render as open and with overrideSelect true
  // In legacy mode, use the open prop and passed overrideSelect value
  const isOpen = isEventBusEnabled && elementRef !== undefined ? true : open;
  const shouldOverrideSelect =
    isEventBusEnabled && elementRef !== undefined ? true : overrideSelect;

  return (
    <Flyout.Root
      open={isOpen}
      modal={false}
      onOpenChange={handleFlyoutClose}
      size="large"
      container={portal}
    >
      {isAddingLanguage || editingLanguage ? (
        <AddLocale
          userProperties={userProperties}
          setIsAddingLanguage={setIsAddingLanguage}
          isEditingLanguage={editingLanguage}
          setEditingLanguage={setEditingLanguage}
          onCreateLocale={onCreateLocale}
          onUpdateLocale={onUpdateLocale}
          locales={localesList}
          overrideSelect={shouldOverrideSelect}
        />
      ) : (
        <>
          <Flyout.Header>
            <Flyout.Title>Localization</Flyout.Title>
            <Flyout.Description>
              Tell us which languages you plan to support, as well as the
              criteria we should use when targeting those speakers.
            </Flyout.Description>
          </Flyout.Header>
          <Flyout.Content>
            <LocalesList
              portal={portal}
              locales={localesList}
              status={status}
              setEditingLanguage={setEditingLanguage}
              onRemoveLocale={handleRemoveLocale}
            />
          </Flyout.Content>
          <Flyout.Footer>
            <Flyout.Actions>
              <Button
                onClick={() => window.open(learnMoreLink, '_blank')}
                variant="tertiary"
              >
                Learn more
                <Icon icon={faExternalLinkAlt} />
              </Button>
              <Button onClick={setIsAddingLanguage} variant="primary">
                <Icon icon={faPlus} />
                Add language
              </Button>
            </Flyout.Actions>
          </Flyout.Footer>
        </>
      )}
    </Flyout.Root>
  );
};

LocalesFlyout.propTypes = {
  learnMoreLink: PropTypes.string,
  locales: PropTypes.arrayOf(LocalesShape),
  userProperties: userPropertiesShape,
  status: PropTypes.string,
  open: PropTypes.bool,
  portal: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
  onClose: PropTypes.func,
  onCreateLocale: PropTypes.func,
  onRemoveLocale: PropTypes.func,
  onUpdateLocale: PropTypes.func,
  overrideSelect: PropTypes.bool,
  onSetLocale: PropTypes.func,
  activeLocale: LocalesShape,
  elementRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
};

const mapStateToProps = (state, ownProps) => ({
  userProperties: selectUserProperties(state),
  activeLocale: selectActiveLocale(state),
  // Use locales from props if provided, otherwise get from Redux state
  locales: ownProps.locales ?? Object.values(selectLocales(state) ?? {}),
});

const mapDispatchToProps = {
  onCreateLocale: createLocale,
  onRemoveLocale: removeLocale,
  onUpdateLocale: updateLocale,
  onSetLocale: setLocale,
};

export default connect(mapStateToProps, mapDispatchToProps)(LocalesFlyout);
